import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Facebook, Globe, PencilIcon, Phone } from "lucide-react";
import { Link } from "react-router-dom";

import BusinessLogo from "@/assets/business-logo.png";
import BusinessBanner from "@/assets/business-banner.png";
import { useState } from "react";
import BusinessInfo from "../../modal/my-business/business-info-form";
import { Organization } from "@/types/organization";
import { useGetBusinessTypeQuery } from "@/services/commonApi";
import { internationalTimezoneOptions, languageOptions } from "@/lib/constants/system-settings/business-info";
const { VITE_VIEW_S3_URL } = import.meta.env;

type BussinessInfoProps = {
  organizationData?: Organization,
  refetch: () => void
}
const BussinessInfo = ({ organizationData, refetch }: BussinessInfoProps) => {
  const [isOpen, setOpen] = useState(false);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [data, setData] = useState<Organization | undefined>(undefined);
  const { data: businessTypeOptions } = useGetBusinessTypeQuery()
  const businessType = businessTypeOptions?.find((business) => business.value == String(organizationData?.business_type_id))?.label
  const timezone = internationalTimezoneOptions?.find(timezone => timezone.value == String(organizationData?.timezone))?.label
  const language = languageOptions?.find(language => language.value == String(organizationData?.language))?.label

  const handleEdit = (data: Organization) => {
    console.log("Edit notification:", data);
    setAction("edit");
    setData(data);
    setOpen(true);
    // Add your edit logic here
  };
  return (
    <Card className="w-full ">
      <CardHeader className="w-full pb-0">
        {/* Hero Image */}
        <div className="shadow-md relative w-full overflow-hidden rounded-md">
          <img
            src={organizationData?.banner_for_admin ? `${VITE_VIEW_S3_URL}/${organizationData?.banner_for_admin}` : BusinessBanner}
            alt="Person working out in gym"
            className=" object-cover w-full h-40 aspect-auto rounded-md"
          />

          <Button
            size="icon"
            variant="ghost"
            className="absolute right-3 top-3 rounded-lg bg-primary p-2 shadow-sm hover:bg-primary/90 h-8 w-8"
            aria-label="Edit"
            onClick={() => handleEdit(organizationData as Organization)}
          >
            <PencilIcon className="h-4 w-4" />
          </Button>
        </div>

        {/* Content Container with Overlapping Logo */}
        <div className="relative -top-[25px] flex items-center gap-6  px-3 py-0 ">
          {/* Logo and Text */}
          <div className="flex items-center gap-4 ">
            <div className="relative size-[110px] overflow-hidden rounded-lg border border-zinc-200 bg-gray-200 shadow-md">
              <img
                src={organizationData?.logo ? `${VITE_VIEW_S3_URL}/${organizationData?.logo}` : BusinessLogo}
                alt={organizationData?.name}
                className="object-cover w-full h-full p-0.5"
              />
            </div>
            <div className="flex flex-col mt-[25px] ">
              <h1 className="text-base font-medium text-zinc-900">{organizationData?.name}</h1>
              <p className="text-sm text-zinc-500 text-nowrap">{businessType}</p>
            </div>
          </div>

          {/* Contact Links */}
          <div className="ml-auto flex flex-wrap items-center justify-end gap-x-6 gap-y-2 mt-[25px]">
            <div className="flex items-center gap-2 text-xs text-zinc-600 hover:text-zinc-900">
              <Phone className="h-4 w-4" />
              <span>{organizationData?.phone}</span>
            </div>

            <div className="flex items-center gap-2 text-xs text-zinc-600 hover:text-zinc-900">
              <Globe className="h-4 w-4" />
              <a href={organizationData?.promotion_url as string} target="_blank" >{organizationData?.promotion_url}</a>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Separator className="h-[1px]" />
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5 mt-2">
          <div className="space-y-1">
            <p className="text-xs ">Description:</p>
            <p className="text-xs text-muted-foreground break-words line-clamp-3">
              {organizationData?.description}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs ">Location</p>
            <p className="text-xs text-muted-foreground">
              {organizationData?.address}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs ">Time Zone:</p>
            <p className="text-xs text-muted-foreground">{timezone}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs ">Language:</p>
            <p className="text-xs text-muted-foreground">{language}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs ">Company Registration:</p>
            <p className="text-xs text-muted-foreground">
              {organizationData?.company_registration}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs ">VAT Registration No.:</p>
            <p className="text-xs text-muted-foreground break-words">
              {organizationData?.vat_number}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs ">Bank Account:</p>
            <p className="text-xs text-muted-foreground break-words">
              {organizationData?.bank_account}
            </p>
          </div>
        </div>
      </CardContent>
      <BusinessInfo
        editModeData={organizationData}
        isOpen={isOpen}
        setOpen={setOpen}
        action={action}
        setAction={setAction}
        refetch={refetch}
      />
    </Card>
  );
};

export default BussinessInfo;
